import "./Pacing.scss";
import {
  awaitJSON,
  MiscLambdaFetch,
  pollS3,
  CrossChannelLambdaFetch,
  S3SignedUrlFetch,
} from "../utils/fetch-utils";
import { DatePicker, FullPageSpinner, Page, TextToggleButton } from "../Components";
import { MdHelp } from "react-icons/md";
import { useSetError } from "../redux/modals";
import * as Dfns from "date-fns/fp";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DateRange } from "../utils/types";
import useLocation from "../utils/hooks/useLocation";
import { useTabbedNav } from "../utils/hooks/useNav";
import { RouteComponentProps, Router } from "@reach/router";
import PacingDashboard from "./PacingDashboard";
import PacingTableDashboard from "./PacingTableDashboard";
import {
  PacingDataEntry,
  CombinedPacingAndBudgetData,
} from "@blisspointmedia/bpm-types/dist/Pacing";
import { Modal } from "react-bootstrap";
import { useCompanyInfo } from "../redux/company";

const enum TabKey {
  PACING = "pacing-bars",
  PERFORMANCE_TABLE = "performance-table",
}

const NAVS = [
  { label: "Pacing", key: TabKey.PACING },
  { label: "Pacing and Performance Table", key: TabKey.PERFORMANCE_TABLE },
];

const DATE_FORMAT = "yyyy-MM-dd";

interface CrossChannelPacingFetchKey {
  key: string;
}

interface CrossChannelPacingData {
  customSegmentNames: string[];
  filteredPacingData: PacingDataEntry[];
  uniquePlatforms: string[];
  sourceOptions: {
    value: string;
    label: string;
  }[];
  pacingTableData: CombinedPacingAndBudgetData[];
  adjustedGranularBudget: PacingDataEntry[];
  groupedTableData: any[];
  pacingTableDataHeaders: any[];
  kpiOptions: {
    label: string;
    value: string;
  }[];
}

const usePacingInfo = (
  dateRange: DateRange,
  company: string,
  kpi: string,
  tablePreset: string,
  sourceType: "Platform" | "Source of Truth",
  setError
) => {
  const [dataReady, setDataReady] = useState(false);
  const [pacingData, setPacingData] = useState<CrossChannelPacingData>({
    customSegmentNames: [],
    filteredPacingData: [],
    uniquePlatforms: [],
    sourceOptions: [],
    pacingTableData: [],
    adjustedGranularBudget: [],
    groupedTableData: [],
    pacingTableDataHeaders: [],
    kpiOptions: [],
  });

  const [lastConfig, setLastConfig] = useState<string | null>(null);
  const { start, end } = dateRange;
  const config = useMemo(() => `${start}_${end}_${kpi}_${tablePreset}_${sourceType}`, [
    start,
    end,
    kpi,
    tablePreset,
    sourceType,
  ]);

  const fetchPacingInfo = useCallback(async () => {
    try {
      setDataReady(false);

      if (process.env.NODE_ENV === "development") {
        const response = await CrossChannelLambdaFetch("/getCrossChannelPacingDashboardInfo", {
          params: {
            start,
            end,
            company: company === "tinuititest" ? "dsw" : company,
            kpi,
            tablePreset,
            sourceType,
            isTest: true,
          },
        });
        const dataKey: CrossChannelPacingFetchKey = await awaitJSON(response);
        let data: CrossChannelPacingData;
        let res = await S3SignedUrlFetch(`bpm-cache/${dataKey.key}`);

        data = await awaitJSON(res);

        setPacingData(data);
      } else {
        const result = await MiscLambdaFetch("/kickOffLambda", {
          method: "POST",
          body: {
            fileType: "txt",
            lambdaArgs: {
              start,
              end,
              company: company === "tinuititest" ? "dsw" : company,
              kpi,
              tablePreset,
              sourceType,
            },
            lambdaName: "crosschannel-getCrossChannelPacingDashboard",
          },
        });

        const uuid = await awaitJSON(result);
        const content = await pollS3({
          autoDownload: false,
          bucket: "bpm-cache",
          filename: `${uuid}.txt`,
          mimeType: "text/plain",
          timeout: 10 * 60,
          period: 2000,
        });
        const textContent = await content.text();
        let data = JSON.parse(textContent) as CrossChannelPacingData;

        setPacingData(data);
      }

      setDataReady(true);
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        title: e instanceof Error ? e.message : String(e),
      });
    }
  }, [company, end, start, kpi, tablePreset, sourceType, setError]);

  useEffect(() => {
    if (start && end && lastConfig !== config) {
      fetchPacingInfo();
      setLastConfig(config);
    }
  }, [config, fetchPacingInfo, lastConfig, start, end]);

  return {
    ...pacingData,
    dataReady,
  };
};

const Pacing: React.FC = ({ navigate }: RouteComponentProps) => {
  const companyInfo = useCompanyInfo();
  const { defaultCrossChannelSource, cid } = companyInfo;
  const { company } = useLocation();
  const setError = useSetError();
  const [dateRange, setDateRange] = useState<DateRange>({
    start: Dfns.format(DATE_FORMAT, Dfns.startOfISOWeek(new Date())),
    end: Dfns.format(DATE_FORMAT, Dfns.addDays(6, Dfns.startOfISOWeek(new Date()))),
  });
  const [tablePreset, setTablePreset] = useState<string>("Channel");
  const [kpi, setKpi] = useState<string>("");

  const [selectedSources, setSelectedSources] = useState<string[]>(() =>
    defaultCrossChannelSource ? [defaultCrossChannelSource] : []
  );

  const platformAsSourceOfData = useMemo(
    () => selectedSources.length === 1 && selectedSources.includes("platform"),
    [selectedSources]
  );

  const {
    customSegmentNames,
    dataReady,
    filteredPacingData,
    uniquePlatforms,
    sourceOptions,
    pacingTableData,
    adjustedGranularBudget,
    groupedTableData,
    pacingTableDataHeaders,
    kpiOptions,
  } = usePacingInfo(
    dateRange,
    company,
    kpi,
    tablePreset,
    platformAsSourceOfData ? "Platform" : "Source of Truth",
    setError
  );

  useEffect(() => {
    if (sourceOptions && sourceOptions.length > 0) {
      const sourceFiltered = sourceOptions.filter(row => row.value === "platform");
      setSelectedSources([sourceFiltered[0].value]);
    }
  }, [sourceOptions]);

  const [dataMode, setDataMode] = useState("Absolute");
  const [simplicity, setSimplicity] = useState("Simple");
  const [showHelpModal, setShowHelpModal] = useState(false);

  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "pacing",
    defaultKey: TabKey.PACING,
    navs: NAVS.map(nav => nav.key, NAVS),
  });

  const [showPacingDataModal, setShowPacingDataModal] = useState(true);

  useEffect(() => {
    setShowPacingDataModal(true);
  }, [dateRange]);

  const progress = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const today = new Date();

    const daysTotal = Dfns.differenceInDays(start, end);
    const daysPassed = Math.max(
      Dfns.differenceInDays(start, today) > daysTotal
        ? daysTotal
        : Dfns.differenceInDays(start, today),
      0
    );

    return (daysPassed / daysTotal) * 100;
  }, [dateRange]);

  return (
    <Page
      app2Redesign
      title="Pacing"
      pageType="Pacing"
      navs={NAVS}
      selectedNav={tab}
      onNav={goToTab}
      actions={
        <div className="pacingDashboardActions">
          {tab === TabKey.PACING && (
            <>
              <MdHelp onClick={() => setShowHelpModal(!showHelpModal)} className="optionsHelp" />
              <TextToggleButton
                design="primary-dark"
                options={["Relative", "Absolute"]}
                selectedOption={dataMode}
                onChange={value => setDataMode(value)}
              />
              <TextToggleButton
                design="primary-dark"
                options={["Simple", "Adjusted"]}
                selectedOption={simplicity}
                onChange={value => setSimplicity(value)}
              />
            </>
          )}
          <DatePicker
            range={dateRange}
            onChange={setDateRange}
            removeHeaderText
            isOutsideRange={() => false}
            isDayBlocked={() => false}
            datePresets={true}
          />
        </div>
      }
    >
      {!dataReady ? (
        <FullPageSpinner />
      ) : !filteredPacingData?.length ? (
        <Modal
          size="lg"
          show={showPacingDataModal}
          onHide={() => setShowPacingDataModal(!showPacingDataModal)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              There is no Pacing Data for this date range, please check the{" "}
              <a
                href={`https://blisspoint.tinuiti.com/${company}/budget-intake-tool`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Budget Intake Tool
              </a>
              .
            </Modal.Title>
          </Modal.Header>
        </Modal>
      ) : (
        <Router className="fullPageRouter brandEquityPage">
          <PacingDashboard
            pacingData={filteredPacingData}
            start={dateRange.start}
            end={dateRange.end}
            simplicity={simplicity}
            customSegments={customSegmentNames}
            isActual={dataMode === "Absolute"}
            showHelpModal={showHelpModal}
            setShowHelpModal={setShowHelpModal}
            path={TabKey.PACING}
            adjustedGranularBudget={adjustedGranularBudget}
          />
          <PacingTableDashboard
            cid={cid}
            progress={progress}
            pacingTableData={pacingTableData}
            customSegments={customSegmentNames}
            sourceValueOptions={sourceOptions}
            platforms={uniquePlatforms}
            path={TabKey.PERFORMANCE_TABLE}
            kpi={kpi}
            setKpi={setKpi}
            tablePreset={tablePreset}
            setTablePreset={setTablePreset}
            selectedSources={selectedSources}
            setSelectedSources={setSelectedSources}
            platformAsSourceOfData={platformAsSourceOfData}
            groupedTableData={groupedTableData}
            pacingTableDataHeaders={pacingTableDataHeaders}
            kpiOptions={kpiOptions}
          />
        </Router>
      )}
    </Page>
  );
};

export default Pacing;
